/**
 * A cohort's sessions in the admin form: generating them, checking them as
 * the operator types, and turning an old weekly group into a list.
 *
 * The server validates again and has the last word (overlaps, bad dates are
 * a 400 naming the row). What is here is what lets the form say so before
 * anybody presses Save. The methodology's calendar rules — forbidden days,
 * 20:00, hours per day — are the server's (`lib/groupCalendar.js`): they
 * depend on the school calendar and on the trainer's other groups.
 *
 * Dates are "YYYY-MM-DD" and times "HH:MM", Romania's wall clock, as stored.
 */

import { splitProblem } from "./groupCalendar";

export const WEEKDAYS = ["Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă", "Duminică"];
export const MAX_SESSIONS = 30;
export const NOTE_MAX = 80;
/** The accredited programme's live hours (backend: COURSE_LIVE_HOURS). */
export const LIVE_HOURS = 15;

export const emptySession = () => ({ date: "", start_time: "", end_time: "", note: "" });

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export function toMinutes(hhmm) {
  if (!TIME.test(hhmm || "")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

function parseDay(iso) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || "")) return null;
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isoOf(d) {
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Monday = 0, as in WEEKDAYS (JS getDay has Sunday = 0). */
export function weekdayOf(iso) {
  const d = parseDay(iso);
  return d ? WEEKDAYS[(d.getDay() + 6) % 7] : null;
}

export function sessionMinutes(s) {
  const a = toMinutes(s.start_time);
  const b = toMinutes(s.end_time);
  return a != null && b != null && b > a ? b - a : 0;
}

export function totalMinutes(sessions) {
  return (sessions || []).reduce((sum, s) => sum + sessionMinutes(s), 0);
}

export function hoursLabel(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m) return `${h} h ${String(m).padStart(2, "0")} min`;
  if (h === 1) return "o oră";
  return h >= 20 ? `${h} de ore` : `${h} ore`;
}

export function sortSessions(sessions) {
  return [...sessions].sort((a, b) =>
    (a.date || "~").localeCompare(b.date || "~") || (a.start_time || "").localeCompare(b.start_time || ""));
}

/**
 * `count` sessions on the chosen weekdays, from `firstDate` onwards, all at
 * the same time — the starting point the operator then edits row by row.
 */
export function generateSessions({ firstDate, days, count, start, end }) {
  const first = parseDay(firstDate);
  const n = Math.min(Math.max(parseInt(count, 10) || 0, 0), MAX_SESSIONS);
  if (!first || !days?.length || !n) return [];
  const out = [];
  const d = new Date(first);
  for (let guard = 0; out.length < n && guard < 400; guard += 1) {
    const iso = isoOf(d);
    if (days.includes(weekdayOf(iso))) {
      out.push({ date: iso, start_time: start || "", end_time: end || "", note: "" });
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export function applyTimeToAll(sessions, { start_time, end_time }) {
  return sessions.map((s) => ({ ...s, start_time, end_time }));
}

/** Reads "18:00 - 20:00", "18.00–20.00", "9:00-13:00". */
export function parseInterval(text) {
  const m = (text || "").match(/(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})/);
  if (!m) return null;
  const p = (h, mm) => `${String(h).padStart(2, "0")}:${mm}`;
  return { start: p(m[1], m[2]), end: p(m[3], m[4]) };
}

/**
 * A proposal for a group still on the weekly pattern: its weekdays between
 * its start and end dates, at its time. Only a proposal — the operator
 * reviews every row before it is saved.
 */
export function sessionsFromWeekly(group) {
  const time = parseInterval(group?.schedule_time) || { start: "", end: "" };
  const days = (group?.schedule_days || []).filter((d) => WEEKDAYS.includes(d));
  const first = parseDay(group?.start_date);
  if (!first || !days.length) {
    return group?.start_date ? [{ ...emptySession(), date: group.start_date, start_time: time.start, end_time: time.end }] : [];
  }
  const last = parseDay(group?.end_date);
  const out = [];
  const d = new Date(first);
  while (out.length < MAX_SESSIONS && (last ? d <= last : out.length < 8)) {
    const iso = isoOf(d);
    if (days.includes(weekdayOf(iso))) out.push({ date: iso, start_time: time.start, end_time: time.end, note: "" });
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/**
 * Per row: the errors that make a row no timetable at all (no date, end
 * before start, two sessions overlapping). Returns `{ errors: {index: msg},
 * blocking }`.
 */
export function checkSessions(sessions) {
  const errors = {};

  (sessions || []).forEach((s, i) => {
    const a = toMinutes(s.start_time);
    const b = toMinutes(s.end_time);
    if (!parseDay(s.date)) errors[i] = "Alege data.";
    else if (a == null || b == null) errors[i] = "Completează orele.";
    else if (b <= a) errors[i] = "Ora de final trebuie să fie după ora de început.";
    else if ((s.note || "").length > NOTE_MAX) errors[i] = `Mențiunea are cel mult ${NOTE_MAX} de caractere.`;
    else if (splitProblem(s, b - a)) errors[i] = splitProblem(s, b - a);
  });

  const byDay = {};
  (sessions || []).forEach((s, i) => { if (!errors[i] && s.date) (byDay[s.date] = byDay[s.date] || []).push(i); });
  Object.values(byDay).forEach((idx) => {
    const rows = idx.map((i) => ({ i, a: toMinutes(sessions[i].start_time), b: toMinutes(sessions[i].end_time) }))
      .sort((x, y) => x.a - y.a);
    for (let k = 1; k < rows.length; k += 1) {
      if (rows[k].a < rows[k - 1].b) errors[rows[k].i] = "Se suprapune cu altă sesiune din aceeași zi.";
    }
  });

  return { errors, blocking: Object.keys(errors).length > 0 };
}

/** Warnings about the whole list, shown under it. */
export function listWarnings(sessions, { liveHours = LIVE_HOURS } = {}) {
  const total = totalMinutes(sessions);
  const out = [];
  if (sessions?.length && total !== liveHours * 60) {
    out.push(`Sesiunile însumează ${hoursLabel(total)}, programul are ${hoursLabel(liveHours * 60)} live.`);
  }
  return out;
}

/** "Luni, 5 octombrie 2026 · 18:00–20:00". */
export function sessionLine(s) {
  const d = parseDay(s.date);
  const when = d
    ? `${weekdayOf(s.date)}, ${d.toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}`
    : s.date;
  return `${when} · ${s.start_time}–${s.end_time}`;
}

/** Whether two session lists describe the same schedule (order-insensitive). */
export function sameSessions(a, b) {
  const key = (list) => sortSessions(list || [])
    .map((s) => [s.date, s.start_time, s.end_time, (s.note || "").trim()].join("|")).join(";");
  return key(a) === key(b);
}
